import React from 'react';
import { InstallPwaButton } from '../InstallPwaButton';

interface CrmSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  isLight: boolean;
  settingsTab: 'personal' | 'general' | 'security' | 'users';
  setSettingsTab: (tab: 'personal' | 'general' | 'security' | 'users') => void;
  setClientTheme: (theme: string) => void;
  clientTimezone: string;
  setClientTimezone: (tz: string) => void;
  pushStatus: 'granted' | 'default' | 'denied' | 'unsupported';
  isPushLoading: boolean;
  subscribeToWebPush: (silent?: boolean) => Promise<boolean>;
  handleTestPushNotification: () => Promise<void>;
  slug?: string;
  onResetDefaults: () => void;
}

export function CrmSettingsDrawer({
  isOpen,
  onClose,
  isLight,
  settingsTab,
  setSettingsTab,
  setClientTheme,
  clientTimezone,
  setClientTimezone,
  pushStatus,
  isPushLoading,
  subscribeToWebPush,
  handleTestPushNotification,
  slug,
  onResetDefaults
}: CrmSettingsDrawerProps) {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(4px)',
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: isLight ? 'white' : '#18181b',
          border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          width: '90%',
          maxWidth: '500px',
          padding: '2rem',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          maxHeight: '85vh',
          overflowY: 'auto'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.2rem', color: isLight ? '#09090b' : 'white' }}>Dashboard Settings</h2>
          <div
            onClick={onClose}
            style={{ cursor: 'pointer', opacity: 0.6 }}
            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', borderBottom: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <button onClick={() => setSettingsTab('personal')} style={{ padding: '0.5rem 1rem', background: 'transparent', border: 'none', borderBottom: settingsTab === 'personal' ? (isLight ? '2px solid #09090b' : '2px solid white') : '2px solid transparent', color: settingsTab === 'personal' ? (isLight ? '#09090b' : 'white') : (isLight ? '#6b7280' : '#a1a1aa'), fontWeight: settingsTab === 'personal' ? 600 : 400, cursor: 'pointer', transition: 'all 0.2s' }}>Personal</button>
          <button onClick={() => setSettingsTab('general')} style={{ padding: '0.5rem 1rem', background: 'transparent', border: 'none', borderBottom: settingsTab === 'general' ? (isLight ? '2px solid #09090b' : '2px solid white') : '2px solid transparent', color: settingsTab === 'general' ? (isLight ? '#09090b' : 'white') : (isLight ? '#6b7280' : '#a1a1aa'), fontWeight: settingsTab === 'general' ? 600 : 400, cursor: 'pointer', transition: 'all 0.2s' }}>General</button>
          <button onClick={() => setSettingsTab('security')} style={{ padding: '0.5rem 1rem', background: 'transparent', border: 'none', borderBottom: settingsTab === 'security' ? (isLight ? '2px solid #09090b' : '2px solid white') : '2px solid transparent', color: settingsTab === 'security' ? (isLight ? '#09090b' : 'white') : (isLight ? '#6b7280' : '#a1a1aa'), fontWeight: settingsTab === 'security' ? 600 : 400, cursor: 'pointer', transition: 'all 0.2s' }}>Security</button>
          <button onClick={() => setSettingsTab('users')} style={{ padding: '0.5rem 1rem', background: 'transparent', border: 'none', borderBottom: settingsTab === 'users' ? (isLight ? '2px solid #09090b' : '2px solid white') : '2px solid transparent', color: settingsTab === 'users' ? (isLight ? '#09090b' : 'white') : (isLight ? '#6b7280' : '#a1a1aa'), fontWeight: settingsTab === 'users' ? 600 : 400, cursor: 'pointer', transition: 'all 0.2s' }}>Users</button>
        </div>

        {settingsTab === 'personal' && (
          <>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: isLight ? '#4b5563' : '#a1a1aa' }}>Theme</label>
              <button
                onClick={() => {
                  const next = isLight ? 'dark' : 'light';
                  setClientTheme(next);
                  try {
                    localStorage.setItem('crm-theme', next);
                    window.dispatchEvent(new Event('storage'));
                  } catch {}
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: isLight ? '#f3f4f6' : 'rgba(255,255,255,0.05)',
                  color: isLight ? '#09090b' : 'white',
                  border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {isLight ? (
                  <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg> Switch to Dark</>
                ) : (
                  <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg> Switch to Light</>
                )}
              </button>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: isLight ? '#4b5563' : '#a1a1aa' }}>Timezone</label>
              <select
                value={clientTimezone || 'local'}
                onChange={(e) => {
                  setClientTimezone(e.target.value);
                  localStorage.setItem('crm-tz', e.target.value);
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.2)',
                  color: isLight ? '#09090b' : 'white',
                  border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="local">Local Browser Time</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Phoenix">Mountain Time - Arizona</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="America/Anchorage">Alaska Time (AKT)</option>
                <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>

            <div style={{ background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: isLight ? '#09090b' : 'white' }}>Push Notifications</span>
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  padding: '0.2rem 0.5rem',
                  borderRadius: '4px',
                  background: pushStatus === 'granted' ? 'rgba(34, 197, 94, 0.15)' : (pushStatus === 'denied' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(234, 179, 8, 0.15)'),
                  color: pushStatus === 'granted' ? '#22c55e' : (pushStatus === 'denied' ? '#ef4444' : '#eab308')
                }}>
                  {pushStatus === 'granted' ? '● Active' : (pushStatus === 'denied' ? 'Blocked' : 'Action Required')}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '0.8rem', color: isLight ? '#6b7280' : '#a1a1aa', lineHeight: 1.4 }}>
                {pushStatus === 'granted'
                  ? 'Device is configured to receive incoming messages, alerts, and call dispatches.'
                  : (pushStatus === 'denied'
                    ? 'Notifications are blocked in your browser or Android settings. Please allow notifications for dark.tel to receive alerts.'
                    : 'Enable background Web Push to receive SMS and message notifications even when the app is closed.')}
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                <button
                  onClick={() => subscribeToWebPush(false)}
                  disabled={isPushLoading}
                  style={{ flex: 1, padding: '0.6rem', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: isPushLoading ? 'wait' : 'pointer', opacity: isPushLoading ? 0.7 : 1 }}
                >
                  {isPushLoading ? 'Connecting...' : (pushStatus === 'granted' ? 'Re-sync Notifications' : 'Enable Notifications')}
                </button>
                {pushStatus === 'granted' && (
                  <button
                    onClick={handleTestPushNotification}
                    disabled={isPushLoading}
                    style={{ padding: '0.6rem 1rem', background: isLight ? '#f3f4f6' : 'rgba(255,255,255,0.1)', color: isLight ? '#09090b' : 'white', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 600, cursor: isPushLoading ? 'wait' : 'pointer' }}
                    title="Send a real-time test notification to this device"
                  >
                    Send Test
                  </button>
                )}
              </div>
            </div>

            <div style={{ marginTop: 'auto', paddingTop: '2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ paddingBottom: '1rem', borderBottom: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)' }}>
                <InstallPwaButton isLight={isLight} />
              </div>
              <button
                onClick={() => {
                  if (slug) localStorage.removeItem(`crm-pw-${slug}`);
                  window.location.reload();
                }}
                style={{ background: '#ef4444', border: 'none', color: 'white', padding: '0.75rem', borderRadius: '6px', fontSize: '0.9rem', cursor: 'pointer', fontWeight: 600 }}
              >
                Log Out
              </button>
              <button
                onClick={onResetDefaults}
                style={{ background: 'transparent', border: 'none', color: isLight ? '#6b7280' : '#a1a1aa', fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Reset to Default
              </button>
            </div>
          </>
        )}

        {settingsTab === 'general' && (
          <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isLight ? '#6b7280' : '#a1a1aa' }}>
            <p>General settings coming soon.</p>
          </div>
        )}

        {settingsTab === 'security' && (
          <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isLight ? '#6b7280' : '#a1a1aa' }}>
            <p>Security settings coming soon.</p>
          </div>
        )}

        {settingsTab === 'users' && (
          <div style={{ minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isLight ? '#6b7280' : '#a1a1aa' }}>
            <p>User management coming soon.</p>
          </div>
        )}
      </div>
    </div>
  );
}
